/**
 * pedal_profile_store — 페달 프로파일(가속/브레이크 곡선)의 경량 외부 스토어.
 *
 * cockpit_speed_limit_store 와 동일한 패턴(useSyncExternalStore 싱글턴)이라
 * 무거운 의존성 없이 편집 UI와 직접제어 훅이 함께 구독할 수 있다.
 * 콕핏 공통 설정이며(차량별 아님) localStorage 에 유지된다.
 */
import { useSyncExternalStore } from "react";
import {
  createDefaultPedalProfile,
  type PedalCurvePoint,
  type PedalKind,
  type PedalProfile,
  PEDAL_PROFILE_POINTS,
  setCurvePointY,
} from "../../utils/pedal_profile";

const STORAGE_KEY = "aceworks.cockpit.pedalProfile.v1";

function isValidCurve(curve: unknown): curve is PedalCurvePoint[] {
  return (
    Array.isArray(curve) &&
    curve.length === PEDAL_PROFILE_POINTS &&
    curve.every(
      (p) =>
        p &&
        typeof p === "object" &&
        Number.isFinite((p as PedalCurvePoint).x) &&
        Number.isFinite((p as PedalCurvePoint).y),
    )
  );
}

function readStoredProfile(): PedalProfile {
  const fallback = createDefaultPedalProfile();
  try {
    if (typeof localStorage === "undefined") return fallback;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      accel: isValidCurve(parsed?.accel) ? parsed.accel : fallback.accel,
      brake: isValidCurve(parsed?.brake) ? parsed.brake : fallback.brake,
    };
  } catch {
    return fallback;
  }
}

let pedalProfile: PedalProfile = readStoredProfile();
const listeners = new Set<() => void>();

function commit(next: PedalProfile): void {
  pedalProfile = next;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  } catch {
    // 저장 실패는 무시 — 이번 세션 동안은 메모리 값으로 동작한다.
  }
  listeners.forEach((listener) => listener());
}

export function getPedalProfileSnapshot(): PedalProfile {
  return pedalProfile;
}

/** 제어점 드래그 — 단조증가/끝점 규칙은 pedal_profile 유틸이 강제한다. */
export function updatePedalCurvePoint(
  kind: PedalKind,
  index: number,
  nextY: number,
): void {
  const curve = setCurvePointY(pedalProfile[kind], index, nextY);
  if (curve === pedalProfile[kind]) {
    return;
  }
  commit({ ...pedalProfile, [kind]: curve });
}

export function setPedalCurve(kind: PedalKind, curve: PedalCurvePoint[]): void {
  commit({ ...pedalProfile, [kind]: curve });
}

export function resetPedalProfile(): void {
  commit(createDefaultPedalProfile());
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function usePedalProfile(): PedalProfile {
  return useSyncExternalStore(
    subscribe,
    getPedalProfileSnapshot,
    getPedalProfileSnapshot,
  );
}
